import { useTranslation } from 'react-i18next';
import { EntityDetailTitle, ScoreBadge, UserCard } from '@bliks-io/ui';
import type { ShowSystemResponse, SystemCapability } from '@bliks-io/contracts';
import { BackButton } from '@/components/back-button';
import { ENTITY_ICONS } from '@/constants/entityIcons';

interface SystemDetailHeaderProps {
  system: ShowSystemResponse['data'];
  slug: string;
  capabilities?: SystemCapability;
}

export function SystemDetailHeader({ system, slug, capabilities }: SystemDetailHeaderProps) {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-between mb-8">
      <div className="flex items-center gap-4">
        <BackButton to="/$slug/organization/systems" params={{ slug }} />
        {system.logo ? (
          <img
            src={
              system.logo.type === 'svg'
                ? system.logo.svg
                : system.logo.thumbnail.webp || system.logo.thumbnail.png
            }
            alt={system.name}
            className="h-10 w-10 object-contain"
          />
        ) : (
          <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center">
            <ENTITY_ICONS.system className="h-5 w-5 text-muted-foreground" />
          </div>
        )}
        <div>
          <EntityDetailTitle name={system.name} deletedAt={system.deletedAt} />
          <p className="text-sm font-normal leading-5 text-gray-8">
            {system.description || t('organization.systems.noDescription')}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-8">
        {capabilities?.canViewSensitiveFields &&
          system.statistics?.averageStepRating !== null &&
          system.statistics?.averageStepRating !== undefined && (
            <ScoreBadge value={system.statistics.averageStepRating} variant="simple" size="lg" />
          )}
        <UserCard
          user={{
            firstName: system.systemOwner.firstName,
            lastName: system.systemOwner.lastName,
            email: system.systemOwner.email,
            avatar: system.systemOwner.avatar,
            role: system.systemOwner.mainRole,
            departmentAbbreviation: system.systemOwner.department?.abbreviation
          }}
          variant="with-role"
          itemVariant="outline"
          size="sm"
          className="min-w-[280px]"
        />
      </div>
    </div>
  );
}
